/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable-next-line react/prop-types */
/*
Author: Lukman Yudokusumo
*/

import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { useDispatch } from 'react-redux';
import Toast from 'react-native-toast-message';
import { getDocumentAsync } from 'expo-document-picker';
import PropTypes from 'prop-types';

import SociollaText from '@components/sociolla/SociollaText';
import { NoTranslate } from '@components/shared/TranslationContext';
import Image from '@components/global/Image';
import FormLabel from '@components/form/common/FormLabel';
import { isWeb } from '@global/screens';

import FormError from '@components/form/common/FormError';
import {
  uploadS3,
  downloadFile,
  isAllowedAttachmentFormat,
} from '@global/helpers';
import { MsOrder } from '@api/ms-order';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import {
  ATTACHMENT_FORMAT_ERROR_TITLE,
  ATTACHMENT_FORMAT_ERROR_DESCRIPTION,
} from '@/constants';
import Colors from '@global/colors';

const propTypes = {
  testID: PropTypes.string,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  containerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  showLabel: PropTypes.bool,
  getDetail: PropTypes.func,
  id: PropTypes.string,
  readOnly: PropTypes.bool,
  onUploadFile: PropTypes.func,
  onRemoveFile: PropTypes.func,
};

const defaultProps = {
  testID: 'attachments-input',
  label: '',
  value: [],
  isRequired: false,
  hasError: false,
  containerStyle: {},
  showLabel: true,
  getDetail: () => {},
  id: '',
  readOnly: false,
};

const AttachmentsInput = ({
  testID = 'attachments-input',
  label,
  isRequired = false,
  value = [],
  onChange,
  hasError,
  containerStyle,
  showLabel = true,
  getDetail = () => {},
  id = '',
  readOnly = false,
  onUploadFile,
  onRemoveFile,
}) => {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const isOtherFilesLabel = [
    'Other Files',
    'Other File',
    'Upload Attachments',
  ].includes(label);

  const confirmConfig = {
    title: 'Remove Document',
    description: 'This document will be deleted. Do you want to proceed?',
  };

  const deleteFile = async (payloadUrl) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      await MsOrder.deleteSkpInternalAttachment(id, { url: payloadUrl });
      getDetail(id);
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      console.log(error);
    }
  };

  const saveFile = async (payloadUrl, fileName) => {
    if (payloadUrl) {
      const attachment = {
        attachment: {
          url: payloadUrl,
          name: fileName,
        },
      };
      await MsOrder.updateSkpInternalAttachment(id, attachment);
      getDetail(id);
    }
  };

  const handleUpload = async () => {
    const file = await getDocumentAsync({
      copyToCacheDirectory: false,
    });
    if (!file.canceled) {
      if (!isAllowedAttachmentFormat(file.assets[0])) {
        dispatch(
          showAlert({
            show: true,
            title: ATTACHMENT_FORMAT_ERROR_TITLE,
            description: ATTACHMENT_FORMAT_ERROR_DESCRIPTION,
            isConfirm: false,
          }),
        );
        return;
      }
      dispatch(showPopup({ name: 'isLoading', value: true }));
      setUploading(true);
      try {
        const url = await uploadS3({
          file: file.assets[0],
          isNeedMobileSupport: true,
        });
        if (url) {
          const updatedAttachments = [
            ...value,
            { name: file.assets[0].name, url },
          ];
          onChange(updatedAttachments);
          if (!showLabel) {
            if (onUploadFile) {
              onUploadFile(url, file.assets[0].name);
            } else {
              saveFile(url, file.assets[0].name);
            }
          }
        } else {
          Toast.show({
            text1: 'Failed to upload file. Please try again.',
            type: 'error',
          });
        }
      } catch (error) {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      } finally {
        setUploading(false);
        dispatch(showPopup({ name: 'isLoading', value: false }));
      }
    }
  };

  const handleRemove = (attachment) => {
    const removeAttachment = () => {
      if (showLabel) {
        const updatedAttachments = value.filter(
          (item) => item.url !== attachment.url,
        );

        onChange(updatedAttachments);
      } else if (onRemoveFile) {
        onRemoveFile(attachment);
      } else {
        deleteFile(attachment.url);
      }
    };

    dispatch(
      showAlert({
        show: true,
        title: confirmConfig.title,
        description: confirmConfig.description,
        onCallback: removeAttachment,
      }),
    );
  };

  return (
    <View testID={testID}>
      {isOtherFilesLabel ? (
        showLabel && <FormLabel label={label} isRequired={isRequired} />
      ) : (
        <NoTranslate>
          {showLabel && <FormLabel label={label} isRequired={isRequired} />}
        </NoTranslate>
      )}
      {!readOnly && (
        <TouchableOpacity
          style={styles.uploadButton}
          onPress={handleUpload}
          disabled={uploading}>
          <SociollaText type="medium">
            {uploading ? 'Uploading...' : 'Upload Files'}
          </SociollaText>
        </TouchableOpacity>
      )}
      <NoTranslate>
        <View style={{ right: !isWeb && !showLabel ? 18 : 0 }}>
          {value.length > 0 && <View style={styles.breaker}></View>}
          <View>
            {value.map((item, index) => (
              <View
                testID={`${testID}-item-${index}`}
                key={`${item?.url || ''}-${index}`}
                style={styles.attachmentItem}>
                <View style={styles.contentNameAttachment}>
                  <SociollaText numberOfLines={1} style={styles.fileName}>
                    Attachment
                  </SociollaText>
                </View>
                <View style={styles.contentUploadButton}>
                  <TouchableOpacity
                    style={styles.downloadButton}
                    onPress={() => {
                      downloadFile(
                        item?.url || '',
                        'attachment',
                        '',
                        true,
                        true,
                      );
                    }}>
                    <Image
                      testID={`${testID}-download-${index}`}
                      style={styles.downloadIcon}
                      source={require('@/assets/img/icons/attachment.png')}
                      isResolveAsset
                    />
                  </TouchableOpacity>
                  {!readOnly && (
                    <TouchableOpacity
                      style={styles.removeButton}
                      onPress={() => handleRemove(item)}>
                      <Image
                        testID={`${testID}-remove-${index}`}
                        style={styles.trashIcon}
                        width={20}
                        height={20}
                        source={require('@assets/img/icons/trash-grey.svg')}
                        svg={true}
                      />
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>
        <FormError hasError={hasError} />
      </NoTranslate>
    </View>
  );
};

const styles = StyleSheet.create({
  breaker: {
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginBottom: 15,
    borderRadius: 8,
  },
  contentNameAttachment: { width: 130, alignItems: 'center', marginTop: 3 },
  contentUploadButton: {
    display: 'flex',
    flexDirection: 'row',
  },
  uploadButton: {
    width: 150,
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grey0,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 15,
  },
  attachmentItem: {
    width: '100%',
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grey0,
    flexDirection: 'row',
    marginBottom: 10,
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
  },
  fileName: {
    flex: 1,
    fontSize: 14,
    marginRight: 10,
  },
  removeButton: {
    marginTop: 2,
    marginLeft: 3,
  },
  trashIcon: {
    width: 20,
    height: 20,
  },
  downloadButton: {
    marginRight: 10,
  },
  downloadIcon: {
    width: 24,
    height: 24,
  },
});

AttachmentsInput.propTypes = propTypes;
AttachmentsInput.defaultProps = defaultProps;

export default React.memo(AttachmentsInput);
